import { useState, useEffect, useRef } from 'react';
import * as OTPAuth from 'otpauth';
import { Copy, ShieldCheck } from '@phosphor-icons/react';
import { toast } from 'sonner';

export function TotpDisplay({ secret, label }) {
  const [code, setCode] = useState('------');
  const [timeLeft, setTimeLeft] = useState(30);
  const [error, setError] = useState(false);
  const intervalRef = useRef(null);

  useEffect(() => {
    if (!secret) { setCode('------'); setError(false); return; }

    // Clean the secret — remove spaces, dashes, make uppercase
    const cleanSecret = secret.replace(/[\s\-]/g, '').toUpperCase();

    if (cleanSecret.length < 8) { setError(true); setCode('------'); return; }

    try {
      const totp = new OTPAuth.TOTP({
        algorithm: 'SHA1',
        digits: 6,
        period: 30,
        secret: OTPAuth.Secret.fromBase32(cleanSecret),
      });

      const generate = () => {
        try {
          const token = totp.generate();
          setCode(token);
          setError(false);
          const now = Math.floor(Date.now() / 1000);
          setTimeLeft(30 - (now % 30));
        } catch (err) {
          setError(true);
          setCode('------');
        }
      };

      generate();
      intervalRef.current = setInterval(() => {
        generate();
      }, 1000);

      return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
    } catch (err) {
      setError(true);
      setCode('------');
    }
  }, [secret]);

  const copyCode = (e) => {
    e?.stopPropagation();
    if (code && code !== '------') {
      navigator.clipboard.writeText(code);
      toast.success('OTP Copied!');
    }
  };

  if (!secret) return null;

  const progressPct = (timeLeft / 30) * 100;

  return (
    <div className="inline-flex items-center gap-2 bg-slate-900 text-white rounded-lg px-3 py-2" data-testid="totp-display">
      <ShieldCheck size={16} weight="fill" className="text-emerald-400 flex-shrink-0" />
      <div className="flex flex-col">
        {label && <span className="text-[9px] text-slate-400 uppercase">{label}</span>}
        <div className="flex items-center gap-2">
          {error ? (
            <span className="text-xs text-red-400">Invalid key</span>
          ) : (
            <>
              <span className="font-mono text-lg font-bold tracking-[0.2em] select-all" data-testid="totp-code">
                {code.slice(0, 3)} {code.slice(3)}
              </span>
              <div className="relative w-6 h-6 flex-shrink-0">
                <svg className="w-6 h-6 -rotate-90" viewBox="0 0 24 24">
                  <circle cx="12" cy="12" r="10" fill="none" stroke="#334155" strokeWidth="2" />
                  <circle cx="12" cy="12" r="10" fill="none" stroke={timeLeft <= 5 ? '#ef4444' : '#10b981'} strokeWidth="2" strokeDasharray={`${progressPct * 0.628} 100`} strokeLinecap="round" />
                </svg>
                <span className="absolute inset-0 flex items-center justify-center text-[9px] font-bold">{timeLeft}</span>
              </div>
            </>
          )}
        </div>
      </div>
      {!error && code !== '------' && (
        <button onClick={copyCode} className="ml-1 text-slate-400 hover:text-white transition" data-testid="totp-copy">
          <Copy size={16} weight="bold" />
        </button>
      )}
    </div>
  );
}
